import { useEffect } from 'react';
import { useStore } from '@tanstack/react-form';
import { getFieldHiddenState } from '../helpers';
import { useDynamicFormStore } from '../store';
import type { DynamicFormField } from '../types';
import type { DynamicFormApi } from './use-app-form';

/**
 * TanStack Form keeps unmounted fields registered and re-runs their last validators on submit, so a hidden required
 * field would still block it. Unregister the field (and its `key.*` sub-fields) and clear their errors; values are kept
 * so the field comes back as it was if it's shown again (remounting re-registers it).
 */
export const releaseHiddenField = (form: DynamicFormApi, fieldKey: string) => {
  Object.keys(form.fieldInfo)
    .filter((name) => name === fieldKey || name.startsWith(`${fieldKey}.`))
    .forEach((name) => {
      // Clear errors first: setting meta re-creates the registry entry, so unregister afterwards.
      if (form.getFieldMeta(name)) {
        form.setFieldMeta(name, (meta) => ({ ...meta, errorMap: {}, errorSourceMap: {} }));
      }
      delete form.fieldInfo[name];
    });
};

// Every mounted field reads the same snapshot, so build the key -> value map once per `formFieldsMap` change.
const fieldValuesCache = new WeakMap<Record<string, DynamicFormField>, Record<string, unknown>>();

const getStoreFieldValues = (formFieldsMap: Record<string, DynamicFormField>) => {
  let values = fieldValuesCache.get(formFieldsMap);
  if (!values) {
    values = Object.fromEntries(Object.values(formFieldsMap).map((field) => [field.key, field.value]));
    fieldValuesCache.set(formFieldsMap, values);
  }
  return values;
};

/** Conditional visibility of a dynamic-form field, kept in sync with the store (review and submit read it). */
export const useConditionalFieldHidden = (form: DynamicFormApi, fieldInfo: DynamicFormField) => {
  const { formFieldsMap, conditionsMap, setFieldHidden } = useDynamicFormStore([
    'formFieldsMap',
    'conditionsMap',
    'setFieldHidden',
  ]);

  const isHidden = useStore(form.store, (state) =>
    fieldInfo.targetedByConditionIds?.length
      ? getFieldHiddenState({
          targetedByConditionIds: fieldInfo.targetedByConditionIds,
          conditionsMap,
          fieldKey: fieldInfo.key,
          values: { ...getStoreFieldValues(formFieldsMap), ...(state.values as Record<string, unknown>) },
          defaultHidden: fieldInfo.hidden,
        })
      : !!fieldInfo.hidden
  );

  useEffect(() => {
    setFieldHidden(fieldInfo.key, isHidden);
    if (isHidden) releaseHiddenField(form, fieldInfo.key);
  }, [form, fieldInfo.key, isHidden, setFieldHidden]);

  return isHidden;
};
